import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigation } from '../../../context/NavigationContext';
import useRequest from "../../../hooks/useRequest";
import { BringzzPageContainer, BringzzText, BringzzIcon, BringzzImage, BringzzButton } from "@bringzz/components";
import AvatarExample from "../../../assets/images/AvatarExample.png";
import DefaultProductImage from 'assets/DefaultProductImage.png';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const PostDetails = () => {
    const { integrationId } = useParams();
    const { sendRequest } = useRequest();
    const { backPress, navigate } = useNavigation();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const parseDate = (dateString) => {
        if (!dateString) return new Date();
        // Handle format "DD.MM.YYYY HH:mm"
        const [datePart, timePart] = dateString.split(' ');
        const [day, month, year] = datePart.split('.');
        const [hours, minutes] = (timePart || '00:00').split(':');
        return new Date(year, month - 1, day, hours, minutes);
    };

    const handleBackPress = () => {
        window.history.back();
    };

    const formatUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        return `https://${url}`;
    };

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const response = await sendRequest({
                    method: 'GET',
                    url: '/module/post/getPostByIntegrationId',
                    params: { integration_id: integrationId }
                });

                if (response.status === 'success') {
                    setPost(response.data);
                } else {
                    setError('Post not found');
                }
            } catch (err) {
                setError('Failed to load post');
                console.error('Error fetching post:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [integrationId, sendRequest]);

    const renderContent = () => {
        if (loading) return (
            <div className="px-4 pt-2">
                <Skeleton circle width={56} height={56} className="mb-4" />
                <Skeleton width={200} height={20} className="mb-2" />
                <Skeleton width={150} height={16} className="mb-6" />
                <Skeleton height={260} className="mb-6 rounded-xl" />
                <Skeleton count={4} className="mb-2" />
            </div>
        );

        if (error) return (
            <div className="flex items-center justify-center min-h-[60vh] px-4">
                <div className="text-center p-6 bg-red-50 rounded-xl max-w-md w-full">
                    <div className="text-red-600 text-sm font-medium">{error}</div>
                </div>
            </div>
        );

        if (!post) return (
            <div className="flex items-center justify-center min-h-[60vh] px-4">
                <div className="text-center p-6 bg-gray-50 rounded-xl max-w-md w-full">
                    <div className="text-gray-600 text-sm font-medium">Post not found</div>
                </div>
            </div>
        );

        return (
            <article className="px-4 pt-2 pb-8">
                <header className="flex items-start mb-8">
                    <img 
                        src={post.user_pic || AvatarExample} 
                        className="w-14 h-14 rounded-full ring-2 ring-white shadow-sm mr-4"
                        alt={`${post.user_first_name}'s avatar`}
                    />
                    <div className="pt-1">
                        <BringzzText 
                            tag="h3" 
                            className="text-lg font-semibold text-gray-900 mb-0.5"
                        >
                            {post.user_first_name} {post.user_last_name}
                        </BringzzText>
                        <BringzzText className="text-sm text-gray-500 font-medium">
                            {parseDate(post.created).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                            })}
                        </BringzzText>
                    </div>
                </header>

                {post.images?.length > 0 && (
                    <figure className="relative aspect-video rounded-2xl overflow-hidden shadow-lg mb-8">
                        <BringzzImage
                            src={post.images[0].content}
                            alt="Featured content"
                            className="w-full h-full object-cover"
                            loading="eager"
                        />
                    </figure>
                )}

                {/* Article Lead-In */}
                {post.type === 1 && post.leadIn && (
                    <div className="mb-6">
                        <BringzzText className="text-gray-700 leading-relaxed">
                            {post.leadIn}
                        </BringzzText>
                    </div>
                )}

                {/* Main Image Caption */}
                {post.type === 1 && post.imageCaption && post.images?.length > 0 && (
                    <div className="mb-6">
                        <BringzzText className="text-gray-600 italic">
                            {post.imageCaption}
                        </BringzzText>
                    </div>
                )}

                {/* Description */}
                <div className="mb-6">
                    <BringzzText className="text-gray-700 leading-relaxed">
                        {post.description}
                    </BringzzText>
                </div>

                {/* Article Content */}
                {post.type === 1 && (post.components?.length > 0 || post.blocks?.length > 0) && (
                    <div className="mb-6">
                        <div className="space-y-6">
                            {(post.components || post.blocks)?.map((block, index) => (
                                <div key={index} className="">
                                    {block.component === "body_copy" && (
                                        <div className="p-3 bg-white border border-gray-200 rounded-lg">
                                            <BringzzText className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                                {block.content}
                                            </BringzzText>
                                        </div>
                                    )}
                                    {block.component === "inline_heading" && (
                                        <div className="p-3 bg-magic-lilac bg-opacity-10 border border-magic-lilac border-opacity-30 rounded-lg">
                                            <BringzzText tag="h5" className="text-lg font-semibold text-gray-900">
                                                {block.content}
                                            </BringzzText>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Tags */}
                {post.type === 1 && post.tags && (
                    <div className="mb-6">
                        <div className="flex flex-wrap gap-2">
                            {post.tags.split(',').map((tag, index) => (
                                <span 
                                    key={index}
                                    className="px-3 py-1 bg-magic-lilac text-white text-sm rounded-full font-medium"
                                >
                                    {tag.trim()}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Product Post Content */}
                {post.type === 3 && (
                    <div className="space-y-6">
                        {/* Products List */}
                        {post.products && post.products.length > 0 && (
                            <div className="mb-6">
                                <div className="space-y-4">
                                    {post.products.map((product, index) => (
                                        <div 
                                            key={index} 
                                            className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-magic-lilac hover:shadow-md transition-all duration-200"
                                            onClick={() => product.integrationId && navigate(`/product/details/${product.integrationId}`)}
                                        >
                                            <div className="flex items-start space-x-4">
                                                <img 
                                                    src={product.productImage || DefaultProductImage} 
                                                    className="w-16 h-16 rounded-lg object-cover bg-gray-100"
                                                    alt={product.productName}
                                                    onError={(e) => {
                                                        if (e.target.src !== DefaultProductImage) {
                                                            e.target.src = DefaultProductImage;
                                                        }
                                                    }}
                                                />
                                                <div className="flex-1">
                                                    <BringzzText tag="h4" className="font-semibold text-gray-900 mb-1">
                                                        {product.productName}
                                                    </BringzzText>
                                                    <BringzzText className="text-sm text-gray-600 mb-2">
                                                        {product.distributorName}
                                                    </BringzzText>
                                                    <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-2">
                                                        <span className="px-2 py-1 bg-gray-100 rounded">
                                                            PZN: {product.pzn}
                                                        </span>
                                                    </div>
                                                    <BringzzText className="text-lg font-semibold text-magic-lilac">
                                                        €{product.avpInEuro?.toFixed(2)}
                                                    </BringzzText>
                                                </div>
                                                {product.integrationId && (
                                                    <BringzzIcon 
                                                        icon="IconChevronRight" 
                                                        folder="LineIcons" 
                                                        size="20" 
                                                        className="text-gray-400 mt-2" 
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <footer className="mt-8 pt-6 border-t border-gray-200">
                    <div className="space-y-2">
                        <BringzzText className="text-sm text-gray-600">
                            <span className="font-medium">Published:</span> {parseDate(post.created).toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                            })}
                        </BringzzText>
                        {post.updated !== post.created && (
                            <BringzzText className="text-sm text-gray-600">
                                <span className="font-medium">Last updated:</span> {parseDate(post.updated).toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                })}
                            </BringzzText>
                        )}
                    </div>
                </footer>
            </article>
        );
    };

    return (
        <BringzzPageContainer
            header={{
                left: (
                    <BringzzIcon
                        size="22"
                        icon="IconChevronLeft"
                        folder="LineIcons"
                        className="cursor-pointer text-gray-900"
                        onClick={handleBackPress}
                    />
                ),
                title: (
                    <BringzzText tag="h2" className="font-semibold text-gray-900">
                        {post?.headline || 'Post Details'}
                    </BringzzText>
                ),
                className: "border-b border-gray-100"
            }}
            containerClass="bg-white"
            footer={
                post?.type === 2 && post?.link ? (
                    <div className="px-4 pb-4">
                        <BringzzButton
                            size="lg"
                            className="bg-magic-lilac w-full flex justify-center text-white"
                            onClick={() => window.open(formatUrl(post.link), '_blank')}
                        >
                            Visit Link
                        </BringzzButton>
                    </div>
                ) : post?.type === 3 && post?.pharmacyUuid ? (
                    <div className="px-4 pb-4">
                        <BringzzButton
                            size="lg"
                            className="bg-magic-lilac w-full flex justify-center text-white"
                            onClick={() => navigate(`/pharmacy/profile/${post.pharmacyUuid}?tab=products`)}
                        >
                            View Pharmacy Products
                        </BringzzButton>
                    </div>
                ) : null
            }
            footerHover={true}
        >
            {renderContent()}
        </BringzzPageContainer>
    );
};

export default PostDetails;